import { Button, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { LinearCountdown } from 'components';
import { useQuizSessionContext } from 'contexts';
import { Question as QuestionFull } from 'types';
import { useCallback, useEffect, useState } from 'react';
import { Question } from 'types';
import { compareTwoStrings } from 'string-similarity';
import { indexToLetter } from 'utils';
import { UserSessionAnswer_quiz_master_quiz_session_user_answers } from 'graphql/queries/types/UserSessionAnswer';

interface Props {
    question: Question;
    questionIndex: number;
    savedAnswer?: UserSessionAnswer_quiz_master_quiz_session_user_answers;
    similarityThreshold?: number;
    onAnswer: (isAnswerCorrect: boolean, question: Question, userAnswer?: string) => void;
}

/*
 * * DONE - Create custom Answer rendering for this use case as we need custom text and only render with options to answer
 */
export const LiveAnswer = (props: Props) => {
    const session = useQuizSessionContext();
    const [questionTimedOut, setQuestionTimedOut] = useState(false);
    const [answer, setAnswer] = useState<string>('');
    const isMultipleChoice = props.question.answers.length > 1;

    useEffect(() => {
        setAnswer(props.savedAnswer?.answer ?? '');
    }, [props]);

    const onSubmitAnswer = useCallback(() => {
        const correctAnswer = props.question.answers.find((a) => a.isCorrect)!;
        if (isMultipleChoice) {
            props.onAnswer(correctAnswer.answer === answer, props.question, answer);
            return;
        }

        const similarity = compareTwoStrings(correctAnswer.answer!.toLowerCase(), answer!.toLowerCase());
        const threshold = props.similarityThreshold ?? 0.75;
        props.onAnswer(similarity >= threshold, props.question, answer);
    }, [answer, isMultipleChoice, props]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !!answer) {
                onSubmitAnswer();
            }
        };

        // attach the event listener
        document.addEventListener('keydown', handleKeyPress);
        // remove the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [answer, onSubmitAnswer, props.onAnswer]);

    const onQuestionTimeout = () => {
        setQuestionTimedOut(true);
    };

    const onSkipQuestion = () => {
        props.onAnswer(false, props.question);
    };

    const disableAnswering =
        questionTimedOut ||
        session?.userSession.hasEnded ||
        (session?.session.questionTimeSec && props.savedAnswer
            ? new Date(props.savedAnswer?.answered_at) > new Date(Date.now() - session?.session.questionTimeSec * 1000)
            : false);

    const renderAnswerButtons = (
        <Stack className="answer-buttons" direction="row" spacing={2}>
            <Button variant="contained" className="incorrect" onClick={onSkipQuestion} disabled={!!disableAnswering}>
                Skip
            </Button>
            <Button variant="contained" className="correct" onClick={onSubmitAnswer} disabled={!answer || !!disableAnswering}>
                Submit
            </Button>
        </Stack>
    );

    const onMultipleChoiceAnswerChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setAnswer(value);
    };

    const renderAnswersRadioButtons = (answers: Pick<QuestionFull, 'answers'>) => {
        return (
            <Stack direction="column" spacing={2} className="answer answers radiobuttons">
                <RadioGroup name="multiple-choice-answers" onChange={onMultipleChoiceAnswerChange}>
                    {answers.answers.map((answerItem, index) => (
                        <FormControlLabel
                            key={`answer-${index}`}
                            value={answerItem.answer}
                            control={<Radio checked={answerItem.answer === answer} disabled={disableAnswering} />}
                            label={`${indexToLetter(index)})`}
                            // label={`${indexToLetter(index)}) ${answerItem.answer}`}
                        />
                    ))}
                </RadioGroup>
                {renderAnswerButtons}
            </Stack>
        );
    };

    const renderAnswerInput = () => (
        <Stack direction="column" spacing={2} className="answer answers input">
            <TextField label="Answer" value={answer} onChange={(event) => setAnswer(event.target.value)} disabled={disableAnswering} />
            {renderAnswerButtons}
        </Stack>
    );

    const renderAnswer = (answers: Pick<QuestionFull, 'answers'>) => {
        if (isMultipleChoice) {
            return renderAnswersRadioButtons(answers);
        }

        return renderAnswerInput();
    };

    return (
        <>
            {!!session?.session.questionTimeSec && (
                <LinearCountdown seconds={session?.session.questionTimeSec} onTimeout={onQuestionTimeout} />
            )}
            <Stack spacing={2} className="answer">
                <Typography variant="h3">{`Question ${props.questionIndex + 1}.`}</Typography>
                {renderAnswer(props.question)}
            </Stack>
        </>
    );
};
